import React, { PureComponent } from "react";
import { Gantt } from "dhtmlx-gantt";
import "../styles/ganttStyles.scss";
import ReactDOMServer from "react-dom/server";
import ReactDOM from "react-dom";
import { withTheme } from "@material-ui/core";
import { compose } from "recompose";
import { utcToZonedTime } from "date-fns-tz";
class GanttReport extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isExpanded: false,
    };
    this.eventIds = [];
    this.gantt = Gantt.getGanttInstance();
  }

  componentDidMount() {
    const { tasks, ganttLayout, showCurrentMarker } = this.props;
    const { gantt } = this;
    this.initializeGanttPluginsAndConfig();
    this.initializeColumns();
    this.renderTasks();
    if (showCurrentMarker) {
      //adds current time reference line on the chart and updates it every minute
      this.addCurrentMarker();
      this.updateMarkerEveryMinute();
      this.highlightCurrentTimeCells();
    }
    this.renderToolTips();
    gantt.config.layout = ganttLayout;
    gantt.ext.fullscreen.getFullscreenElement = () => {
      return this.ganttContainer;
    };
    this.initializeGanttEvents();
    gantt.ext.zoom.init(this.getZoomConfig());
    const { startDate, endDate } = this.getGanttTimeRange();
    gantt.init(this.ganttContainer, startDate, endDate);

    gantt.ext.zoom.setLevel(2); //setting default zoom level
    tasks && gantt.parse(tasks);
    gantt.ext.tooltips.tooltipFor({
      // this is to prevent tooltips that are rendered on the grid section.
      selector: ".gantt_row",
      html: function (event, domElement) {
        return "";
      },
    });
  }

  componentDidUpdate(prevProps) {
    const { gantt } = this;
    if (prevProps.isFullScreen !== this.props.isFullScreen) {
      gantt.ext.fullscreen.toggle();
    }
    const { tasks, showCurrentMarker } = this.props;
    this.saveScrollState();
    gantt.clearAll();
    showCurrentMarker && this.addCurrentMarker();
    tasks && gantt.parse(tasks);
    this.restoreScrollState();
    gantt.render();
  }

  componentWillUnmount() {
    const { gantt } = this;
    clearInterval(this.interval);
    this.eventIds.map((eventId) => gantt.detachEvent(eventId));
    this.state.isExpanded && gantt.ext.fullscreen.collapse();
    gantt.destructor();
  }

  initializeGanttPluginsAndConfig = () => {
    const { gantt } = this;
    gantt.plugins({
      marker: true,
      tooltip: true,
      fullscreen: true,
      multiselect: true,
    });
    gantt.config.initial_scroll = false;
    gantt.config.grid_width = 320;
    gantt.config.readonly = this.props.isReadOnly;
    gantt.config.static_background = true;
    gantt.config.date_format = "%Y-%m-%d %H:%i";
  };

  initializeGanttEvents = () => {
    const { gantt } = this;
    this.eventIds.push(
      gantt.attachEvent("onExpand", () => {
        this.setState({
          isExpanded: true,
        });
      })
    );
    this.eventIds.push(
      gantt.attachEvent("onCollapse", () => {
        this.setState({
          isExpanded: false,
        });
      })
    );
    this.eventIds.push(
      gantt.attachEvent("onGanttReady", () => {
        this.props.onGanttReady?.(gantt);
      })
    );
    this.eventIds.push(
      gantt.attachEvent("onBeforeDataRender", () => {
        this.props.onBeforeDataRender?.();
      })
    );
    this.eventIds.push(
      gantt.attachEvent("onTaskClick", (taskId) => {
        // Select/Unselect all child tasks associated with the selected/clicked parent task.
        this.toggleAllChildrenSelection(taskId);
        return true;
      })
    );
  };

  toggleAllChildrenSelection = (taskId) => {
    const { gantt } = this;
    gantt.getChildren(taskId).forEach((child) => {
      gantt.toggleTaskSelection(child);
    });
  };

  getFormattedDate = (date, hour = 0, min = 0) => {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hour,
      min
    );
  };

  getUTCDate = (dateString = Date.now()) => {
    const date = new Date(dateString);

    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    );
  };

  getCurrentTime = () => {
    return utcToZonedTime(new Date(), this.props.terminalOffset);
  };

  getGanttTimeRange = () => {
    if (this.props.isRealtimeReport) {
      const currentDateTime = this.getCurrentTime();
      const nextDateTime = this.gantt.date.add(currentDateTime, 1, "day");
      return {
        startDate: this.getFormattedDate(currentDateTime),
        endDate: this.getFormattedDate(nextDateTime, 12, 0),
      };
    }
    const nextDateTime = this.gantt.date.add(
      this.getUTCDate(this.props.startDate),
      1,
      "day"
    );
    return {
      startDate: this.getFormattedDate(this.getUTCDate(this.props.startDate)),
      endDate: this.getFormattedDate(nextDateTime, 12, 0),
    };
  };

  getMinutesfromTimeString = (str) => {
    if (str) {
      const time = str.split(":");
      return time[0] * 60 + Number(time[1]);
    }
    return 0;
  };

  checkCurrentTimeSlot = (currentTime, leftBoundTime, rightBoundTime) => {
    const now = this.getMinutesfromTimeString(currentTime);
    const leftBoundTimeMinutes = this.getMinutesfromTimeString(leftBoundTime);
    const rightBoundTimeMinutes = this.getMinutesfromTimeString(rightBoundTime);
    if (now >= leftBoundTimeMinutes && now <= rightBoundTimeMinutes) {
      return true;
    }
    return false;
  };

  highlightCurrentTimeCells = () => {
    // highlight the current time cell using a different color/style
    const { gantt } = this;
    gantt.templates.timeline_cell_class = (task, date) => {
      const currentZoomLevel =
        this.props.zoomLevels[gantt.ext.zoom.getCurrentLevel()][0];
      const currentDateTime = gantt.getMarker(this.currentMarker).start_date;
      const formatFunc = gantt.date.date_to_str("%H:%i");
      if (
        currentDateTime.getDate() === date.getDate() &&
        this.checkCurrentTimeSlot(
          formatFunc(currentDateTime),
          formatFunc(date),
          formatFunc(
            gantt.date.add(date, currentZoomLevel.step, currentZoomLevel.unit)
          )
        )
      ) {
        return "currentTimeCell";
      }

      if (date.getHours() % 2) {
        return "oddTimeLineCell";
      }
      return "";
    };
  };

  saveScrollState = () => {
    const { gantt } = this;
    const { x, y } = gantt.getScrollState();
    gantt.scrollPosX = x;
    gantt.scrollPosY = y;
  };

  restoreScrollState = () => {
    const { scrollPosX, scrollPosY } = this.gantt;
    this.gantt.scrollTo(scrollPosX, scrollPosY);
  };

  getZoomConfig = () => {
    // this method intializes the gantt zoom levels received as props.
    const { zoomLevels } = this.props;
    const { gantt } = this;
    return {
      levels: zoomLevels,
      trigger: "wheel",
      useKey: "ctrlKey",
      element: () => {
        return gantt.$root.querySelector(".gantt_task");
      },
    };
  };

  initializeColumns = () => {
    const { gantt } = this;
    // the external render config is used here to render React components in gantt grid columns.
    gantt.config.external_render = {
      isElement: (element) => {
        return React.isValidElement(element);
      },
      renderElement: (element, container) => {
        ReactDOM.render(element, container);
      },
    };
  };

  renderToolTips = () => {
    // this method converts React components to static markup and renders them as tooltip.
    this.gantt.templates.tooltip_text = (start, end, task) => {
      const html = ReactDOMServer.renderToStaticMarkup(
        this.props.renderStopPopup(task)
      );
      return html;
    };
  };

  compare_input = (id, filterData) => {
    const { gantt } = this;
    var match = false;
    // render all child tasks if parent task is filtered from recevied search string from props.
    if (gantt.hasChild(id)) {
      gantt.eachTask((child_object) => {
        if (this.compare_input(child_object.id, filterData)) match = true;
      }, id);
    }
    if (
      gantt.getTask(id).text?.toLowerCase().includes(filterData.toLowerCase())
    )
      match = true;

    return match;
  };

  all_parents_check = (id, filterData) => {
    // render all parent tasks if child task is filtered from recevied search string from props.
    const { gantt } = this;
    var show_task = false;
    gantt.eachParent((task) => {
      if (
        gantt
          .getTask(task.id)
          .text?.toLowerCase()
          .includes(filterData.toLowerCase())
      )
        show_task = true;
    }, id);
    return show_task;
  };

  renderTasks = () => {
    const { gantt } = this;
    this.eventIds.push(
      gantt.attachEvent("onBeforeTaskDisplay", (id, task) => {
        if (task.type === "project" || gantt.isSplitTask(task)) {
          task.hide_bar = true;
          //this hides the bar for parent task on the chart to match our UI requirement.
        }
        if (
          this.compare_input(id, this.props.searchText) ||
          this.all_parents_check(id, this.props.searchText)
        ) {
          return true;
        }
        return false;
      })
    );
  };

  getCurrentMarkerText = (dateTime) => {
    return (
      "Current " +
      "<div>" +
      this.gantt.date.date_to_str("%H:%i")(dateTime) +
      "</div>"
    );
  };

  addCurrentMarker = () => {
    const currentDateTime = this.getCurrentTime();
    this.currentMarker = this.gantt.addMarker({
      start_date: currentDateTime,
      css: "today",
      text: this.getCurrentMarkerText(currentDateTime),
      title: "Current Time",
    });
  };

  updateMarkerEveryMinute = () => {
    const { gantt } = this;
    this.interval = setInterval(() => {
      const today = gantt.getMarker(this.currentMarker);
      if (today) {
        const date = this.getCurrentTime();
        today.start_date = date;
        today.text = this.getCurrentMarkerText(date);
        gantt.updateMarker(this.currentMarker);
        gantt.refreshData();
      }
    }, 60000);
  };

  zoomIn = () => {
    this.gantt.ext.zoom.zoomOut();
  };

  zoomOut = () => {
    this.gantt.ext.zoom.zoomIn();
  };

  render() {
    return (
      <React.Fragment>
        <div
          id="ganttView"
          className={`ganttTheme-${this.props.theme.themeName}`}
        >
          <div
            id="ganttContainer"
            ref={(input) => {
              this.ganttContainer = input;
            }}
            className={`gantt-theme-material theme-${
              this.props.theme.themeName
            } ${this.state.isExpanded ? "fullScreen" : ""}`}
          ></div>
          <div id="zoomControl">
            <span id="zoomIn" onClick={this.zoomIn}></span>
            <span id="zoomOut" onClick={this.zoomOut}></span>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default compose(withTheme())(GanttReport);
